/**
 * Collapsible panel listing all currently-uploaded vendor files.
 *
 * Default collapsed. Shows filename, detected type/rooftop, and age.
 */
import React, { useState } from "react"
import { ChevronDown, ChevronRight, X } from "lucide-react"

import { PhotoListFileStatus, deletePhotoListFile } from "../../api/photoList"
import { THEME } from "../../config/theme"
import { useDealerGroup } from "../../context/DealerGroupContext"
import { useIsMobile } from "../../hooks/useIsMobile"
import { formatAge } from "../../utils/formatAge"

interface Props {
  fileStatus: PhotoListFileStatus | null
  // Called after a file is successfully deleted so the parent can refresh
  // file-status and the panel re-renders with the file removed.
  onFileDeleted?: () => void
}

interface FlatFile {
  filename: string
  type: string
  rooftop?: string
  ageSeconds?: number
  fresh?: boolean
}

export default function ActiveVendorFilesPanel({ fileStatus, onFileDeleted }: Props) {
  // File storage is per dealer group — deletes must target the active group's
  // partition. The page already requires a concrete group to render.
  const { dealerGroupId } = useDealerGroup()
  const [expanded, setExpanded] = useState(false)
  const [deletingFilename, setDeletingFilename] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // The 5-column table can't fit a phone, and the panel's overflow:hidden
  // would clip it silently — mobile renders two-line rows instead.
  const isMobile = useIsMobile()

  const flatFiles = flattenFiles(fileStatus)

  const handleDelete = async (filename: string) => {
    if (!window.confirm(`Delete "${filename}"?`)) return
    setDeletingFilename(filename)
    setDeleteError(null)
    try {
      await deletePhotoListFile(filename, dealerGroupId)
      onFileDeleted?.()
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Delete failed")
    } finally {
      setDeletingFilename(null)
    }
  }

  return (
    <div
      style={{
        border: `1px solid ${THEME.colors.silver}`,
        borderRadius: THEME.borderRadius.md,
        backgroundColor: THEME.colors.neutral.light.surface,
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: THEME.spacing.sm,
          padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
          cursor: "pointer",
          userSelect: "none",
          fontSize: 13,
          fontWeight: 600,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        <span style={{ width: 14, display: "inline-flex", alignItems: "center", color: THEME.colors.neutral.light.textSecondary }}>
          {expanded ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
        </span>
        Active Vendor Files
        <span
          style={{
            fontWeight: 400,
            color: THEME.colors.neutral.light.textSecondary,
            marginLeft: 4,
          }}
        >
          ({flatFiles.length})
        </span>
      </div>

      {expanded && (
        <div
          style={{
            padding: `0 ${THEME.spacing.md} ${THEME.spacing.md}`,
          }}
        >
          {deleteError && (
            <div
              style={{
                background: "rgba(220, 38, 38, 0.08)",
                color: THEME.colors.semantic.danger,
                border: "0.5px solid rgba(220, 38, 38, 0.3)",
                padding: "8px 12px",
                borderRadius: THEME.borderRadius.md,
                marginBottom: 8,
                fontSize: 12,
              }}
            >
              {deleteError}
            </div>
          )}
          {flatFiles.length === 0 ? (
            <div
              style={{
                fontSize: 13,
                color: THEME.colors.neutral.light.textSecondary,
              }}
            >
              No files uploaded today
            </div>
          ) : isMobile ? (
            <div>
              {flatFiles.map((f, i) => {
                const isDeleting = deletingFilename === f.filename
                const metaLine = [
                  f.type,
                  f.rooftop,
                  f.ageSeconds != null
                    ? formatAge(f.ageSeconds) + (f.fresh === false ? " (stale)" : "")
                    : null,
                ]
                  .filter(Boolean)
                  .join("  ·  ")
                return (
                  <div
                    key={`${f.filename}-${i}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: THEME.spacing.sm,
                      padding: "6px 0",
                      borderBottom: `1px solid ${THEME.colors.silver}`,
                      opacity: isDeleting ? 0.5 : 1,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        title={f.filename}
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: THEME.colors.neutral.light.textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {f.filename}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color:
                            f.fresh === false
                              ? THEME.colors.semantic.warning
                              : THEME.colors.neutral.light.textSecondary,
                        }}
                      >
                        {metaLine}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDelete(f.filename)}
                      disabled={isDeleting}
                      title="Delete"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        flexShrink: 0,
                        background: "none",
                        border: "none",
                        color: THEME.colors.semantic.danger,
                        cursor: isDeleting ? "not-allowed" : "pointer",
                        padding: "2px 6px",
                      }}
                    >
                      <X size={16} strokeWidth={2} />
                    </button>
                  </div>
                )
              })}
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
              }}
            >
              <thead>
                <tr
                  style={{
                    borderBottom: `1px solid ${THEME.colors.silver}`,
                    textAlign: "left",
                  }}
                >
                  <th style={thStyle}>Filename</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Rooftop</th>
                  <th style={thStyle}>Age</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {flatFiles.map((f, i) => {
                  const isDeleting = deletingFilename === f.filename
                  return (
                    <tr
                      key={`${f.filename}-${i}`}
                      style={{
                        backgroundColor:
                          i % 2 === 0
                            ? THEME.auditTable.zebraLight
                            : THEME.auditTable.zebraMain,
                        opacity: isDeleting ? 0.5 : 1,
                      }}
                    >
                      <td style={tdStyle} title={f.filename}>
                        {f.filename.length > 60
                          ? f.filename.slice(0, 60) + "..."
                          : f.filename}
                      </td>
                      <td style={tdStyle}>{f.type}</td>
                      <td style={tdStyle}>{f.rooftop ?? "\u2014"}</td>
                      <td
                        style={{
                          ...tdStyle,
                          color:
                            f.fresh === false
                              ? THEME.colors.semantic.warning
                              : undefined,
                        }}
                      >
                        {f.ageSeconds != null ? formatAge(f.ageSeconds) : "\u2014"}
                        {f.fresh === false ? " (stale)" : ""}
                      </td>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() => handleDelete(f.filename)}
                          disabled={isDeleting}
                          title="Delete"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            background: "none",
                            border: "none",
                            color: THEME.colors.semantic.danger,
                            cursor: isDeleting ? "not-allowed" : "pointer",
                            padding: "2px 6px",
                          }}
                        >
                          <X size={16} strokeWidth={2} />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const thStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontWeight: 500,
  fontSize: 12,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const tdStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  color: THEME.colors.neutral.light.textPrimary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: 300,
}

function flattenFiles(fileStatus: PhotoListFileStatus | null): FlatFile[] {
  if (!fileStatus) return []
  const result: FlatFile[] = []
  const { files } = fileStatus

  // Homenet
  if (files.homenet && files.homenet.status !== "missing") {
    result.push({
      filename: files.homenet.filename ?? "Homenet",
      type: "Homenet",
      ageSeconds: files.homenet.age_seconds,
      fresh: files.homenet.fresh,
    })
  }

  // Stargard files
  if (files.stargard?.files) {
    for (const sf of files.stargard.files) {
      result.push({
        filename: sf.filename,
        type: "Stargard",
        rooftop: sf.rooftop,
        ageSeconds: sf.age_seconds,
        fresh: sf.fresh,
      })
    }
  }

  // AutoExact
  if (files.autoexact && files.autoexact.status !== "missing") {
    result.push({
      filename: files.autoexact.filename ?? "AutoExact",
      type: "AutoExact",
      ageSeconds: files.autoexact.age_seconds,
      fresh: files.autoexact.fresh,
    })
  }

  // Dealer Daily Toyota
  if (files.dealer_daily_toyota && files.dealer_daily_toyota.status !== "missing") {
    result.push({
      filename: files.dealer_daily_toyota.filename ?? "Dealer Daily",
      type: "Dealer Daily",
      ageSeconds: files.dealer_daily_toyota.age_seconds,
      fresh: files.dealer_daily_toyota.fresh,
    })
  }

  // Dealer Connect — one entry per rooftop
  const dcStatus = fileStatus.dealer_connect
  if (dcStatus?.rooftops) {
    for (const rt of dcStatus.rooftops) {
      if (rt.status !== "missing" && rt.filename) {
        result.push({
          filename: rt.filename,
          type: rt.label,
          ageSeconds: rt.age_seconds ?? undefined,
          fresh: rt.fresh,
        })
      }
    }
  }

  return result
}
